import { Link } from "@tanstack/react-router";
import { LogOutIcon, MonitorIcon, MoonStarIcon, SettingsIcon, SunIcon } from "lucide-react";

import { useTheme } from "@/components/theme/use-theme";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuGroup,
	DropdownMenuItem,
	DropdownMenuRadioGroup,
	DropdownMenuRadioItem,
	DropdownMenuSub,
	DropdownMenuSubContent,
	DropdownMenuSubTrigger,
	DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/components/ui/lib/utils";
import { Skeleton } from "@/components/ui/skeleton";
import { useSignOut } from "@/hooks/auth/use-signout";
import { useSession } from "@/lib/auth-client";
import * as m from "@/paraglide/messages";

import { AccountSwitcher } from "./account-switcher";
import { OrganizationSwitcher } from "./organization-switcher";
import { PlanCreditsCard } from "./plan-credits-card";

export function UserDropdown({
	className,
	children,
}: {
	className?: string;
	children?: React.ReactNode;
}) {
	const { data, isPending } = useSession();
	const { mutate: signOut } = useSignOut();

	if (isPending) {
		return (
			<div className="flex items-center space-x-2">
				<Skeleton className="w-8 h-8 rounded-full" />
			</div>
		);
	}

	return (
		<DropdownMenu>
			<DropdownMenuTrigger className={cn("w-fit outline-none", className)}>
				<Avatar className="h-full w-full size-8">
					<AvatarImage src={data?.user.image || undefined} className="h-full w-full" />
					<AvatarFallback>CN</AvatarFallback>
				</Avatar>
			</DropdownMenuTrigger>
			<DropdownMenuContent className="min-w-56">
				{data?.user.email && (
					<DropdownMenuGroup>
						<div className="flex cursor-text items-center text-foreground rounded-sm px-2 py-1.5 text-xs outline-none space-x-2">
							<span className="truncate">{data.user.email}</span>
						</div>
					</DropdownMenuGroup>
				)}
				<PlanCreditsCard />
				<AccountSwitcher />
				<OrganizationSwitcher />
				<DropdownMenuGroup>
					<DropdownMenuItem
						className="text-xs"
						render={
							<Link to={"/settings"}>
								<SettingsIcon className="w-4 h-4" /> {m.nav_settings()}
							</Link>
						}
					/>
				</DropdownMenuGroup>
				<ThemeSubMenu />
				{children}
				<DropdownMenuGroup>
					<DropdownMenuItem className="text-xs" onClick={() => signOut()}>
						<LogOutIcon className="w-4 h-4" /> {m.user_signOut()}
					</DropdownMenuItem>
				</DropdownMenuGroup>
			</DropdownMenuContent>
		</DropdownMenu>
	);
}

function ThemeSubMenu() {
	const { themeMode, resolvedTheme, setTheme } = useTheme();

	return (
		<DropdownMenuSub>
			<DropdownMenuSubTrigger className="text-xs">
				{resolvedTheme === "dark" ? (
					<MoonStarIcon className="w-4 h-4" />
				) : (
					<SunIcon className="w-4 h-4" />
				)}
				{m.user_theme()}
			</DropdownMenuSubTrigger>
			<DropdownMenuSubContent>
				<DropdownMenuRadioGroup
					value={themeMode}
					onValueChange={(value) => setTheme(value as "light" | "dark" | "system")}
				>
					<DropdownMenuRadioItem className="text-xs" value="light">
						<SunIcon className="w-4 h-4" /> {m.user_themeLight()}
					</DropdownMenuRadioItem>
					<DropdownMenuRadioItem className="text-xs" value="dark">
						<MoonStarIcon className="w-4 h-4" /> {m.user_themeDark()}
					</DropdownMenuRadioItem>
					<DropdownMenuRadioItem className="text-xs" value="system">
						<MonitorIcon className="w-4 h-4" /> {m.user_themeSystem()}
					</DropdownMenuRadioItem>
				</DropdownMenuRadioGroup>
			</DropdownMenuSubContent>
		</DropdownMenuSub>
	);
}
